import { api } from '@/api'
import type {
  ActivateTrackingObjectRequest,
  TrackingObject,
  TrackingObjectConfig,
  TrackingObjectInfo,
  TrackingObjectResponse,
  TrackingObjectsResponse,
} from '../types'

interface ConfigResponse {
  trackingObjectConfig: TrackingObjectConfig | null
}

// GET /bff/customers/{id}/trackingobjects → { trackingObjects }. The BFF turns
// a missing customer (404 from accounts) into an empty list, so the client
// never has to special-case it.
export function listTrackingObjects(customerId: string): Promise<TrackingObject[]> {
  return api
    .get<TrackingObjectsResponse>(`/customers/${customerId}/trackingobjects`)
    .then((data) => data.trackingObjects)
}

// POST /bff/customers/{id}/trackingobjects → { trackingObject }. A customer
// with no active applications comes back as 400 { code: 'no_active_applications' }.
export function activateTrackingObject(
  customerId: string,
  req: ActivateTrackingObjectRequest,
): Promise<TrackingObject> {
  // req is exactly the wire body (ActivateTrackingObjectRequest); the
  // form→request mapping happens once, in the TrackerActivation screen.
  return api
    .post<TrackingObjectResponse>(`/customers/${customerId}/trackingobjects`, { body: req })
    .then((data) => data.trackingObject)
}

// GET /bff/customers/{id}/trackingobjects/{tid} → { trackingObject }.
export function getTrackingObject(customerId: string, tid: string): Promise<TrackingObject> {
  return api
    .get<TrackingObjectResponse>(`/customers/${customerId}/trackingobjects/${tid}`)
    .then((data) => data.trackingObject)
}

// DELETE /bff/customers/{id}/trackingobjects/{tid} → { trackingObject }.
export function deactivateTrackingObject(customerId: string, tid: string): Promise<TrackingObject> {
  return api
    .delete<TrackingObjectResponse>(`/customers/${customerId}/trackingobjects/${tid}`)
    .then((data) => data.trackingObject)
}

// POST /bff/customers/{id}/trackingobjects/{tid}/move → { trackingObject }.
// Moves the object from customer {id} to targetCid.
export function moveTrackingObject(customerId: string, tid: string, targetCid: string): Promise<TrackingObject> {
  return api
    .post<TrackingObjectResponse>(`/customers/${customerId}/trackingobjects/${tid}/move`, {
      body: { id: tid, targetCid },
    })
    .then((data) => data.trackingObject)
}

// GET /bff/customers/{id}/trackingobjects/{tid}/status → bare TrackingObjectInfo.
export function getTrackingObjectStatus(customerId: string, tid: string): Promise<TrackingObjectInfo> {
  return api.get<TrackingObjectInfo>(`/customers/${customerId}/trackingobjects/${tid}/status`)
}

// GET .../config → { trackingObjectConfig } (null when the object has no config yet).
export function getTrackingObjectConfig(
  customerId: string,
  tid: string,
): Promise<TrackingObjectConfig | null> {
  return api
    .get<ConfigResponse>(`/customers/${customerId}/trackingobjects/${tid}/config`)
    .then((data) => data.trackingObjectConfig)
}

// PUT .../config — whole-document write (ChangeConfigRequest is the config itself).
export function updateTrackingObjectConfig(
  customerId: string,
  tid: string,
  config: TrackingObjectConfig,
): Promise<TrackingObjectConfig> {
  return api
    .put<ConfigResponse>(`/customers/${customerId}/trackingobjects/${tid}/config`, { body: config })
    .then((data) => data.trackingObjectConfig as TrackingObjectConfig)
}
